import { Button } from "@car/ui/components/button";
import { FileDropzone, UploadList, useUploads } from "@car/ui/components/file-upload";
import { FormAlert, useAppForm } from "@car/ui/components/form";
import { Skeleton } from "@car/ui/components/skeleton";
import { optionalText, requiredChoice, requiredText } from "@car/ui/lib/form-rules";
import { SEP } from "@car/ui/lib/utils";
import {
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  Copy01Icon,
  Message01Icon,
  PlusSignIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  PanelEmpty,
  PanelQueryError,
  RatingStars,
  TicketStatusBadge,
} from "@/components/panel-bits";
import {
  TicketSteps,
  TicketThread,
  messageCountText,
  nextStepText,
} from "@/components/ticket-bits";
import { faDate, toFaDigits } from "@/lib/format";
import {
  MAX_TICKET_FILES,
  ticketCloseReasonLabels,
  ticketTypeLabels,
  type SupportTicket,
} from "@/lib/panel";
import { uploadPolicy } from "@/lib/uploads";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/tickets/$ticketId")({
  head: () => ({ meta: [{ title: "تیکت پشتیبانی | پنل مشتریان تابان خودرو" }] }),
  component: TicketPage,
});

const ticketFilePolicy = uploadPolicy("ticket-file");

function TicketPage() {
  const { ticketId } = Route.useParams();
  const query = useQuery(
    orpc.panel.tickets.byId.queryOptions({
      input: { id: ticketId },
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    }),
  );

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }
  if (query.isError) return <PanelQueryError title="تیکت بارگیری نشد" query={query} />;
  const ticket = query.data;
  if (!ticket) {
    return (
      <PanelEmpty
        icon={Message01Icon}
        title="این تیکت پیدا نشد"
        description="شاید نشانی را اشتباه باز کرده‌اید یا تیکت حذف شده است."
        action={
          <Button nativeButton={false} variant="outline" render={<Link to="/panel/tickets" />}>
            همه تیکت‌ها
          </Button>
        }
      />
    );
  }

  const canWrite = ticket.status !== "closed" || ticket.canReopen;

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="justify-self-start"
        nativeButton={false}
        render={<Link to="/panel/tickets" />}
      >
        <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={1.8} />
        همه تیکت‌ها
      </Button>

      <header className="grid gap-4 rounded-2xl border bg-card p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid min-w-0 gap-1.5">
            <h1 className="text-xl font-black text-balance sm:text-2xl">{ticket.subject}</h1>
            <p className="text-sm leading-7 text-muted-foreground">
              <TrackingNumber number={ticket.number} />
              {SEP}
              {ticketTypeLabels[ticket.type]}
              {SEP}
              ثبت {faDate(ticket.createdIso)}
              {SEP}
              {messageCountText(ticket.messageCount)}
              {ticket.vehicleName ? `${SEP}${ticket.vehicleName}` : ""}
              {ticket.invoiceTitle ? `${SEP}فاکتور ${ticket.invoiceTitle}` : ""}
            </p>
          </div>
          <TicketStatusBadge status={ticket.status} />
        </div>
        <TicketSteps status={ticket.status} />
        <p className="text-sm leading-6" role="status">
          {nextStepText(ticket)}
          {ticket.status === "closed" && ticket.closeReason ? (
            <span className="text-muted-foreground">
              {" "}
              (بسته‌شده {ticket.closedIso ? `در ${faDate(ticket.closedIso)}` : ""}؛{" "}
              {ticketCloseReasonLabels[ticket.closeReason]})
            </span>
          ) : null}
        </p>
      </header>

      <section aria-label="گفتگو" className="grid gap-5 rounded-2xl border bg-card p-4 sm:p-5">
        <TicketThread messages={ticket.messages} />
        {ticket.status === "closed" && ticket.rating === null ? (
          <RatingForm ticketId={ticket.id} />
        ) : null}
        {ticket.status === "closed" && ticket.rating !== null ? (
          <div className="grid gap-1 border-t pt-4 text-sm text-muted-foreground">
            <p className="flex items-center gap-2">
              امتیاز شما: <RatingStars value={ticket.rating} size="size-4" />
            </p>
            {ticket.ratingComment ? <p className="leading-7">«{ticket.ratingComment}»</p> : null}
          </div>
        ) : null}
        {canWrite ? (
          <ReplyForm ticket={ticket} />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <p className="text-sm text-muted-foreground">
              موضوع تازه‌ای دارید؟ برایش تیکت جدید ثبت کنید.
            </p>
            <Button nativeButton={false} render={<Link to="/panel/tickets/new" />}>
              <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
              تیکت تازه
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

/** «شماره پیگیری ۱۲۴» that copies itself, for quoting on the phone */
function TrackingNumber({ number }: { number: number }) {
  const text = toFaDigits(String(number));
  return (
    <button
      type="button"
      onClick={() =>
        void navigator.clipboard
          .writeText(String(number))
          .then(() => toast.success("شماره پیگیری کپی شد"))
          .catch(() => undefined)
      }
      className="inline-flex items-center gap-1 rounded-md font-bold text-foreground underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:ring-ring/40 focus-visible:outline-none"
      aria-label={`شماره پیگیری ${text}، برای کپی بزنید`}
    >
      شماره پیگیری {text}
      <HugeiconsIcon
        icon={Copy01Icon}
        strokeWidth={1.8}
        className="size-3.5 text-muted-foreground"
      />
    </button>
  );
}

const replySchema = z.object({ text: requiredText("متن پاسخ", { min: 2, max: 1000 }) });

/**
 * The customer's reply with attachments; on an answered or recently closed ticket it opens
 * it again. «مشکلم حل شد» closes it.
 */
function ReplyForm({ ticket }: { ticket: SupportTicket }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const uploads = useUploads({ policy: ticketFilePolicy, maxFiles: MAX_TICKET_FILES });
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.panel.tickets.key() });

  const reply = useMutation(orpc.panel.tickets.reply.mutationOptions());
  const close = useMutation(
    orpc.panel.tickets.close.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok) return toast.error(result.reason);
        toast.success("تیکت بسته شد؛ خوشحالیم که مشکل حل شد");
        refresh();
      },
      onError: () => toast.error("بستن تیکت انجام نشد؛ دوباره تلاش کنید."),
    }),
  );

  const form = useAppForm({
    defaultValues: { text: "" },
    validators: { onBlur: replySchema },
    onSubmit: async ({ value, formApi }) => {
      setError("");
      const fileIds = uploads.items.flatMap((item) => item.result?.id ?? []);
      const result = await reply
        .mutateAsync({ ticketId: ticket.id, text: value.text.trim(), fileIds })
        .catch(() => null);
      if (!result) return setError("ارسال انجام نشد؛ دوباره تلاش کنید.");
      if (!result.ok) return setError(result.reason);
      toast.success(
        ticket.status === "closed" ? "پاسخ ثبت شد و تیکت دوباره باز شد" : "پاسخ شما ثبت شد",
      );
      formApi.reset();
      for (const item of uploads.items) uploads.dismiss(item.id);
      refresh();
    },
  });

  return (
    <form.AppForm>
      <form.Form className="grid gap-3 border-t pt-4">
        <form.AppField name="text">
          {(field) => (
            <field.TextareaField
              label={ticket.status === "closed" ? "مشکل ادامه دارد؟" : "پاسخ شما"}
              maxLength={1000}
              rows={3}
              placeholder={
                ticket.status === "closed"
                  ? "بنویسید چه چیزی هنوز درست نشده تا تیکت دوباره باز شود."
                  : ticket.status === "answered"
                    ? "اگر پاسخ کافی نبود، همین‌جا ادامه دهید."
                    : "توضیح یا مدرک تازه‌ای دارید؟ همین‌جا اضافه کنید."
              }
            />
          )}
        </form.AppField>
        <FileDropzone
          uploads={uploads}
          multiple
          maxFiles={MAX_TICKET_FILES}
          variant="compact"
          title="افزودن عکس یا PDF"
        />
        <UploadList uploads={uploads} />
        <FormAlert message={error} />
        <div className="flex flex-wrap-reverse items-center justify-between gap-2">
          {ticket.status !== "closed" ? (
            <Button
              type="button"
              variant="ghost"
              disabled={close.isPending}
              onClick={() => close.mutate({ ticketId: ticket.id })}
              className="text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800 dark:text-emerald-400"
            >
              <HugeiconsIcon icon={CheckmarkCircle02Icon} strokeWidth={1.8} />
              مشکلم حل شد، بستن تیکت
            </Button>
          ) : (
            <span />
          )}
          <form.SubmitButton disabled={uploads.busy} pending={reply.isPending}>
            {ticket.status === "closed" ? "ارسال و بازکردن دوباره" : "ارسال پاسخ"}
          </form.SubmitButton>
        </div>
      </form.Form>
    </form.AppForm>
  );
}

const ratingSchema = z.object({
  rating: requiredChoice("امتیاز"),
  comment: optionalText("نظر", 500),
});

/** One short survey after closing: 1–5 stars and an optional sentence */
function RatingForm({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const mutation = useMutation(orpc.panel.tickets.rate.mutationOptions());
  const form = useAppForm({
    defaultValues: { rating: "", comment: "" },
    validators: { onBlur: ratingSchema },
    onSubmit: async ({ value }) => {
      setError("");
      const result = await mutation
        .mutateAsync({ ticketId, rating: Number(value.rating), comment: value.comment.trim() })
        .catch(() => null);
      if (!result) return setError("ثبت امتیاز انجام نشد؛ دوباره تلاش کنید.");
      if (!result.ok) return setError(result.reason);
      toast.success("از نظر شما سپاسگزاریم");
      queryClient.invalidateQueries({ queryKey: orpc.panel.tickets.key() });
    },
  });
  return (
    <form.AppForm>
      <form.Form className="grid gap-3 rounded-2xl bg-muted/50 p-4">
        <form.AppField name="rating">
          {(field) => (
            <field.FormField label="از پاسخ‌گویی ما چقدر راضی بودید؟">
              {({ controlProps }) => (
                <div
                  id={controlProps.id}
                  aria-invalid={controlProps["aria-invalid"]}
                  aria-describedby={controlProps["aria-describedby"]}
                >
                  <RatingStars
                    value={Number(field.state.value) || 0}
                    onChange={(star) => {
                      field.handleChange(String(star));
                      field.handleBlur();
                    }}
                    size="size-7"
                  />
                </div>
              )}
            </field.FormField>
          )}
        </form.AppField>
        <form.AppField name="comment">
          {(field) => <field.TextareaField label="نظر شما (اختیاری)" rows={2} maxLength={500} />}
        </form.AppField>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {error ? <FormAlert message={error} /> : <span />}
          <form.SubmitButton size="sm" pending={mutation.isPending}>
            ثبت امتیاز
          </form.SubmitButton>
        </div>
      </form.Form>
    </form.AppForm>
  );
}
